import { Switch, Route, Redirect } from 'wouter';
import { lazy, Suspense, useEffect } from 'react';
import { queryClient } from './lib/queryClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthProvider } from './hooks/useAuth';
import { CartProvider } from './contexts/CartContext';
import { WalletProvider } from './providers/WalletProvider';

import ErrorBoundary from '@/components/ErrorBoundary';
import AuthErrorBoundary from '@/components/AuthErrorBoundary';
import ProtectedRoute from '@/components/ProtectedRoute';
import { ProactiveTokenRefresh } from '@/components/ProactiveTokenRefresh';
import { TimezoneAutoDetect } from '@/components/TimezoneAutoDetect';
import { ImpersonationBanner } from '@/components/admin/ImpersonationBanner';
import { SiteBanner } from '@/components/SiteBanner';
import NotFound from '@/pages/not-found';
import PublicNotFound from '@/pages/PublicNotFound';
import { useAuth } from '@/hooks/useAuth';

// Smart 404 component that shows appropriate page based on auth status
function NotFoundWrapper() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <NotFound /> : <PublicNotFound />;
}

// Note: Global error handlers (including chunk load recovery) are in main.tsx.
// Do NOT add .catch() fallbacks to lazy imports — they swallow the rejection
// and prevent the global chunk-reload recovery from firing (BUG-139).

// ponytail: WRA-specific pages (Cinema, Equity, Trusts, LawfulDocs, Shop, Cart,
// Donations, Sessions, Consultations, etc.) are disabled for BRITE — lazy decls
// and routes removed together. Page files stay on disk; restore from git if needed.

// Lazy load components for better performance
const Landing = lazy(() => import('@/pages/Landing'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const Courses = lazy(() => import('@/pages/Courses'));
const CourseDetail = lazy(() => import('@/pages/CourseDetail'));
const CourseBookReader = lazy(() => import('@/pages/CourseBookReader'));
const LibraryPage = lazy(() => import('@/pages/LibraryPage'));
const Account = lazy(() => import('@/pages/Account'));
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'));
const PasswordGenerator = lazy(() => import('@/pages/PasswordGenerator'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const RequestPasswordReset = lazy(() => import('@/pages/RequestPasswordReset'));
const SignIn = lazy(() => import('@/pages/SignIn'));
const AdminApp = lazy(() => import('@/pages/AdminApp'));
const EpubUploadWizard = lazy(() => import('@/components/EpubUploadWizard'));
const BookMetadataEditor = lazy(() => import('@/components/BookMetadataEditor'));
const AdminBookViewer = lazy(() => import('@/components/admin/AdminBookViewer'));
const UnifiedCourseEditor = lazy(() => import('@/pages/UnifiedCourseEditor'));
const UnifiedRegister = lazy(() => import('@/pages/UnifiedRegister'));
const EmailRegister = lazy(() => import('@/pages/EmailRegister'));
const TermsOfService = lazy(() => import('@/pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const StyleGuide = lazy(() => import('@/pages/StyleGuide'));
const LoginDebug = lazy(() => import('@/pages/LoginDebug'));
const AdminTagManagement = lazy(() => import('@/pages/AdminTagManagement'));
const AdminEmailTemplates = lazy(() => import('@/pages/AdminEmailTemplates'));
const AdminBunnyDebug = lazy(() => import('@/pages/admin/AdminBunnyDebug'));
const CommunityCalendar = lazy(() => import('@/pages/CommunityCalendar'));
const DonatePage = lazy(() => import('@/pages/DonatePage'));
const BRITEPool = lazy(() => import('@/pages/BRITEPool'));
const BRITEPoolPhase2 = lazy(() => import('@/pages/BRITEPoolPhase2'));
const GeneratorPage = lazy(() => import('@/pages/GeneratorPage'));
const HelpContact = lazy(() => import('@/pages/HelpContact'));
const Feedback = lazy(() => import('@/pages/Feedback'));

// Loading component
function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
    </div>
  );
}

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/" component={Landing} />
        <Route path="/dashboard">
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        </Route>
        <Route path="/notifications">
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        </Route>
        <Route path="/courses">
          <ProtectedRoute>
            <Courses />
          </ProtectedRoute>
        </Route>
        <Route path="/course/:courseSlug">
          <ProtectedRoute>
            <CourseDetail />
          </ProtectedRoute>
        </Route>
        <Route path="/course/:courseSlug/:lessonSlug/read/:bookSlug">
          <ProtectedRoute>
            <CourseBookReader />
          </ProtectedRoute>
        </Route>
        <Route path="/course/:courseSlug/:lessonSlug?">
          <ProtectedRoute>
            <CourseDetail />
          </ProtectedRoute>
        </Route>
        <Route path="/calendar">
          <ProtectedRoute>
            <CommunityCalendar />
          </ProtectedRoute>
        </Route>
        <Route path="/library">
          <ProtectedRoute>
            <LibraryPage />
          </ProtectedRoute>
        </Route>
        <Route path="/book/:bookSlug">
          <ProtectedRoute>
            <LibraryPage />
          </ProtectedRoute>
        </Route>
        <Route path="/generator">
          <ProtectedRoute requireManager>
            <GeneratorPage />
          </ProtectedRoute>
        </Route>
        <Route path="/donate">
          <ProtectedRoute>
            <DonatePage />
          </ProtectedRoute>
        </Route>
        <Route path="/membership">{() => <Redirect to="/donate" />}</Route>
        <Route path="/account">
          <ProtectedRoute>
            <Account />
          </ProtectedRoute>
        </Route>
        <Route path="/account/:userId">
          <ProtectedRoute>
            <Account />
          </ProtectedRoute>
        </Route>
        <Route path="/pool" component={BRITEPool} />
        <Route path="/pool/phase2/:id" component={BRITEPoolPhase2} />
        <Route path="/enroll" component={UnifiedRegister} />
        <Route path="/enroll/email" component={EmailRegister} />
        <Route path="/sign-in" component={SignIn} />
        <Route path="/login">{() => <Redirect to="/sign-in" />}</Route>
        <Route path="/terms-of-service" component={TermsOfService} />
        <Route path="/privacy-policy" component={PrivacyPolicy} />
        <Route path="/help" component={HelpContact} />
        <Route path="/contact" component={HelpContact} />
        <Route path="/support">{() => <Redirect to="/help" />}</Route>
        <Route path="/not-found" component={NotFoundWrapper} />

        <Route path="/verify-email" component={VerifyEmail} />
        <Route path="/feedback/:token" component={Feedback} />
        <Route path="/reset-password" component={RequestPasswordReset} />
        <Route path="/reset-password/confirm" component={ResetPassword} />
        <Route path="/password-generator">
          <ProtectedRoute requireAdmin>
            <PasswordGenerator />
          </ProtectedRoute>
        </Route>
        <Route path="/style">
          <ProtectedRoute requireAdmin>
            <StyleGuide />
          </ProtectedRoute>
        </Route>
        <Route path="/admin">
          <AuthErrorBoundary>
            <ProtectedRoute requireAdmin>
              <AdminApp />
            </ProtectedRoute>
          </AuthErrorBoundary>
        </Route>
        <Route path="/admin/library/books/:id/view">
          <AuthErrorBoundary>
            <ProtectedRoute requireInstructor>
              <AdminBookViewer />
            </ProtectedRoute>
          </AuthErrorBoundary>
        </Route>
        <Route path="/admin/library/books/:id/edit">
          <AuthErrorBoundary>
            <ProtectedRoute requireInstructor>
              <BookMetadataEditor />
            </ProtectedRoute>
          </AuthErrorBoundary>
        </Route>
        <Route path="/admin/library/books/new">
          <AuthErrorBoundary>
            <ProtectedRoute requireInstructor>
              <EpubUploadWizard />
            </ProtectedRoute>
          </AuthErrorBoundary>
        </Route>
        <Route path="/admin/library/books/:slug/edit">
          <AuthErrorBoundary>
            <ProtectedRoute requireInstructor>
              <BookMetadataEditor />
            </ProtectedRoute>
          </AuthErrorBoundary>
        </Route>
        <Route path="/admin/courses/new">
          <AuthErrorBoundary>
            <ProtectedRoute requireInstructor>
              <UnifiedCourseEditor />
            </ProtectedRoute>
          </AuthErrorBoundary>
        </Route>
        <Route path="/admin/courses/:id/edit">
          <AuthErrorBoundary>
            <ProtectedRoute requireInstructor>
              <UnifiedCourseEditor />
            </ProtectedRoute>
          </AuthErrorBoundary>
        </Route>
        <Route path="/admin/tags">
          <AuthErrorBoundary>
            <ProtectedRoute requireAdmin>
              <AdminTagManagement />
            </ProtectedRoute>
          </AuthErrorBoundary>
        </Route>
        <Route path="/admin/email-templates">
          <AuthErrorBoundary>
            <ProtectedRoute requireAdmin>
              <AdminEmailTemplates />
            </ProtectedRoute>
          </AuthErrorBoundary>
        </Route>
        <Route path="/admin/bunny-debug">
          <AuthErrorBoundary>
            <ProtectedRoute requireAdmin>
              <AdminBunnyDebug />
            </ProtectedRoute>
          </AuthErrorBoundary>
        </Route>
        <Route path="/login-debug" component={LoginDebug} />
        <Route path="*" component={NotFoundWrapper} />
      </Switch>
    </Suspense>
  );
}

/**
 * Render the app's main content: a full-page layout, the Router container, and the global Toaster.
 */
function AppContent() {
  const { isImpersonating } = useAuth();

  // Set CSS variable for nav offset when impersonating
  // This pushes all navbars down below the impersonation banner
  useEffect(() => {
    document.documentElement.style.setProperty('--nav-offset', isImpersonating ? '48px' : '0px');
  }, [isImpersonating]);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden overflow-y-auto">
      {/* Proactive Token Refresh - Automatically refreshes tokens before expiry (with circuit breaker protection) */}
      <ProactiveTokenRefresh />
      <TimezoneAutoDetect />

      {/* Impersonation Banner - Shows when admin is impersonating a user */}
      <ImpersonationBanner />

      {/* Site-wide Banners - Admin-created banners from Communications */}
      <SiteBanner />

      <div className="relative z-10">
        <Router />
      </div>

      <Toaster />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <WalletProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <CartProvider>
              <TooltipProvider>
                <AppContent />
              </TooltipProvider>
            </CartProvider>
          </AuthProvider>
        </QueryClientProvider>
      </WalletProvider>
    </ErrorBoundary>
  );
}

export default App;
